import { Heart, Phone, Users, FileCheck, Home, Baby } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const Adoption = () => {
  return (
    <GenericPracticePage
      title="Adoption Attorney"
      subtitle="Guiding families through adoption and parental termination proceedings in Texas. Making your family legally complete with compassionate, experienced representation."
      icon={<Heart className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Texas Adoption Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Adoption is a beautiful way to expand your family and provide a loving home for a child. However, the legal process is complex, requiring careful attention to Texas law and proper procedures. Whether you're adopting your stepchild, a relative's child, or a child through an agency, we guide you through every step.
            </p>
            <p className="text-lg text-muted-foreground">
              We also handle termination of parental rights proceedings when necessary to protect children and finalize adoptions. Our goal is to make your family legally whole while ensuring children's best interests are protected.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Adoption in Texas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Baby className="h-10 w-10 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Stepparent Adoption</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  The most common type in Texas. When you marry someone with children, you can adopt their children if the other biological parent's rights are terminated or they consent.
                </p>
                <div className="bg-accent/10 p-3 rounded mt-3">
                  <p className="text-xs font-semibold mb-1">Requirements:</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Married to the child's biological parent</li>
                    <li>• Child lived with you for at least 6 months</li>
                    <li>• Other parent consents OR rights terminated</li>
                  </ul>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Users className="h-10 w-10 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Relative (Kinship) Adoption</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Grandparents, aunts/uncles, or other relatives adopting a child when biological parents cannot or will not care for them.
                </p>
                <div className="bg-accent/10 p-3 rounded mt-3">
                  <p className="text-xs font-semibold mb-1">Common Scenarios:</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Parents deceased or incarcerated</li>
                    <li>• Parents struggling with addiction</li>
                    <li>• Child already living with relative</li>
                  </ul>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Home className="h-10 w-10 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Private Agency Adoption</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Working through a licensed private adoption agency to adopt an infant or child. Agency handles matching, home study, and counseling.
                </p>
                <div className="bg-accent/10 p-3 rounded mt-3">
                  <p className="text-xs font-semibold mb-1">Our Role:</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Review adoption plan and contracts</li>
                    <li>• Represent you in court</li>
                    <li>• Finalize adoption legally</li>
                  </ul>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <FileCheck className="h-10 w-10 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Foster Care Adoption</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Adopting a child from the Texas foster care system after parental rights have been terminated by CPS.
                </p>
                <div className="bg-accent/10 p-3 rounded mt-3">
                  <p className="text-xs font-semibold mb-1">Benefits:</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Low or no cost</li>
                    <li>• Adoption subsidies available</li>
                    <li>• Medicaid for child</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">The Texas Adoption Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Home Study</h3>
                  <p className="text-sm text-muted-foreground">Licensed social worker evaluates your home, background, finances, and parenting capability. Includes interviews, home visit, and background checks.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Termination of Parental Rights (if needed)</h3>
                  <p className="text-sm text-muted-foreground">If other parent's rights haven't been terminated, this must happen first (through consent or involuntary termination).</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Filing Adoption Petition</h3>
                  <p className="text-sm text-muted-foreground">We prepare and file the adoption petition with the court, including all required documentation.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Background Checks & Clearances</h3>
                  <p className="text-sm text-muted-foreground">Criminal background checks, child abuse registry checks, and other clearances for all adults in the home.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">5</div>
                <div>
                  <h3 className="font-bold mb-1">Six-Month Residence Requirement</h3>
                  <p className="text-sm text-muted-foreground">Child must live with you for at least 6 months before adoption can be finalized (some exceptions for relatives).</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">6</div>
                <div>
                  <h3 className="font-bold mb-1">Final Adoption Hearing</h3>
                  <p className="text-sm text-muted-foreground">Court hearing where judge reviews the case and issues final adoption decree. Child's name can be changed if desired.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">7</div>
                <div>
                  <h3 className="font-bold mb-1">New Birth Certificate</h3>
                  <p className="text-sm text-muted-foreground">After adoption, Texas issues a new birth certificate listing adoptive parents as parents.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Termination of Parental Rights</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                For adoption to occur (except when both parents are deceased), parental rights must be terminated either voluntarily or involuntarily.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold mb-2">Voluntary Termination (Consent)</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Parent voluntarily gives up parental rights by signing an affidavit of relinquishment or consent to adoption.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> Can be revoked within 11 days in some circumstances.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Involuntary Termination</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Court terminates rights against parent's will due to abandonment, neglect, abuse, failure to support, or other statutory grounds.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> Very difficult - requires clear and convincing evidence.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Adult Adoption in Texas</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Texas allows adoption of adults (18+) in certain circumstances, creating a legal parent-child relationship. Common scenarios:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Formalizing a longstanding parent-child relationship (e.g., stepparent who raised the person)</li>
                <li>• Estate planning purposes</li>
                <li>• Immigration benefits</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                <strong>Requirements:</strong> Adult being adopted must consent, and adoption must be in adult's best interest.
              </p>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Post-Adoption Contact Agreements</h2>
            <p className="text-muted-foreground mb-4">
              In some cases, biological parents and adoptive parents agree to ongoing contact (letters, photos, visits). While these agreements can be included in the adoption order, they're difficult to enforce in court.
            </p>
            <p className="text-muted-foreground">
              We can help negotiate and draft these agreements to protect everyone's interests while maintaining flexibility.
            </p>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Ready to Expand Your Family?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today for compassionate, experienced adoption representation. We'll guide you through every step of making your family complete.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default Adoption;
